import {
  Block,
  BlockFooter,
  List,
  ListInput,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Preloader,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { inputToMinor, moneyToInput } from '@/utils/money';
import { isDecimalInput, isIntegerInput, sanitizeNumericInput } from '@/utils/numericInput';
import {
  MapboxPolygonEditor,
  toPolygonRing,
  zoneColor,
  zoneColorMap,
  type PolygonRing,
  type ReferenceZone,
} from '../components/MapboxPolygonEditor';
import {
  useCreateDeliveryZone,
  useDeliveryZones,
  useUpdateDeliveryZone,
} from '../hooks/useOrderSetup';
import type { DeliveryZone } from '@restaurant/api-client';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

// Slide-in editor for a map-drawn (POLYGON) delivery zone - draw the area on a
// map, set its fee/minimum/travel. Distance tiers use DeliveryZoneFormPage.
export default function DeliveryZoneMapFormPage({
  f7route,
}: {
  f7route: { params: { zoneId?: string } };
}) {
  const { zoneId } = f7route.params;
  const isNew = !zoneId || zoneId === 'new';
  const t = useT();
  const { data: zones } = useDeliveryZones();
  const submitRef = useRef<() => void>(() => {});
  // Save stays greyed until the area is drawn, named, and given a minimum order,
  // so an incomplete zone can't be saved (fee is optional → blank saves as 0).
  const [canSave, setCanSave] = useState(false);

  const zf = t.orderSetup.page.zoneMapForm;
  const zone = isNew ? null : (zones?.find((z) => z.id === zoneId) ?? null);
  const ready = isNew || zone != null;
  const back = () => f7.views.current.router.back();

  return (
    <Page name="delivery-zone-map-form" pageContent={false}>
      <Navbar title={isNew ? zf.newTitle : zf.editTitle} backLink={t.back}>
        <NavRight>
          <F7Link
            onClick={ready && canSave ? () => submitRef.current() : undefined}
            style={{ opacity: ready && canSave ? 1 : 0.4 }}
          >
            {t.saveShort}
          </F7Link>
        </NavRight>
      </Navbar>
      {ready ? (
        <MapZoneContent
          zone={zone}
          onDone={back}
          submitRef={submitRef}
          onValidityChange={setCanSave}
        />
      ) : (
        <PageContent>
          <Block className="text-align-center" style={{ paddingTop: 48 }}>
            <Preloader />
          </Block>
        </PageContent>
      )}
    </Page>
  );
}

function MapZoneContent({
  zone,
  onDone,
  submitRef,
  onValidityChange,
}: {
  zone: DeliveryZone | null;
  onDone: () => void;
  submitRef: { current: () => void };
  onValidityChange: (valid: boolean) => void;
}) {
  const zf = useT().orderSetup.page.zoneMapForm;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const lat = useRestaurantStore((s) => s.restaurantConfig?.latitude);
  const lng = useRestaurantStore((s) => s.restaurantConfig?.longitude);
  const center = lat != null && lng != null ? { latitude: lat, longitude: lng } : null;
  const create = useCreateDeliveryZone();
  const update = useUpdateDeliveryZone();
  const { data: zones } = useDeliveryZones();

  // Each POLYGON zone gets its own stable color (by displayOrder) so every area
  // is visually distinct, not just by label - and matches the section preview.
  const colorByZoneId = zoneColorMap(
    (zones ?? [])
      .filter((z) => z.mode === 'POLYGON')
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((z) => z.id),
  );
  const colorOf = (id: string) => colorByZoneId.get(id) ?? zoneColor(0);

  // Other already-drawn polygon zones - shown as colored reference areas so the
  // owner sees what's already covered while drawing this one. (No distance
  // circles: this map is delivery ZONES only.)
  const otherZones: ReferenceZone[] = (zones ?? [])
    .filter((z) => z.mode === 'POLYGON' && z.id !== zone?.id)
    .map((z) => ({
      id: z.id,
      label: z.label,
      ring: toPolygonRing(z.polygon),
      color: colorOf(z.id),
    }))
    .filter((z): z is ReferenceZone => z.ring != null && z.ring.length >= 3);

  // Seed from `zone` on the FIRST render - not in an effect. The map inits once
  // on mount and captures `ring` in its load handler's closure; a later effect
  // setting it wouldn't reach that closure, so the editable polygon would never
  // draw (you'd only see the other zones' reference outlines). MapZoneContent is
  // only mounted once the zone is loaded, so `zone` is available here.
  const [label, setLabel] = useState(() => zone?.label ?? '');
  const [fee, setFee] = useState(() => (zone ? moneyToInput(zone.feeCents, currency) : ''));
  const [minOrder, setMinOrder] = useState(() =>
    zone ? moneyToInput(zone.minOrderCents, currency) : '',
  );
  const [deliveryMins, setDeliveryMins] = useState(() =>
    zone?.deliveryMinutes != null ? String(zone.deliveryMinutes) : '',
  );
  const [ring, setRing] = useState<PolygonRing | null>(() =>
    zone ? toPolygonRing(zone.polygon) : null,
  );
  // In-flight guard for the save button (see submitRef below) - synchronous so a
  // rapid double-tap can't create two zones before the mutation state updates.
  const submittingRef = useRef(false);

  // Save requires a named area, a drawn polygon (≥3 points), and a minimum order.
  // Fee is optional - blank saves as 0 (free delivery for this area).
  const canSave =
    label.trim() !== '' &&
    ring != null &&
    ring.length >= 3 &&
    minOrder.trim() !== '' &&
    deliveryMins.trim() !== '';
  useEffect(() => {
    onValidityChange(canSave);
  }, [canSave, onValidityChange]);

  submitRef.current = () => {
    // Synchronous double-submit guard: a fast double-tap fires two clicks before
    // React re-renders, so the mutation's async isPending hasn't flipped yet -
    // without this, both taps call create.mutate and two zones get saved.
    if (submittingRef.current) return;

    const name = label.trim();
    if (!name) {
      f7.dialog.alert(zf.nameRequired);
      return;
    }
    if (!ring || ring.length < 3) {
      f7.dialog.alert(zf.drawRequired);
      return;
    }
    if (minOrder.trim() === '') {
      f7.dialog.alert(zf.minOrderRequired);
      return;
    }
    if (![fee, minOrder].every(isDecimalInput)) {
      f7.dialog.alert(zf.invalidNumber);
      return;
    }
    if (!isIntegerInput(deliveryMins)) {
      f7.dialog.alert(zf.wholeMinutes);
      return;
    }
    const mins = Number.parseInt(deliveryMins, 10);
    if (!Number.isFinite(mins) || mins < 0) {
      f7.dialog.alert(zf.deliveryTimeRequired);
      return;
    }
    const shared = {
      label: name,
      feeCents: inputToMinor(fee, currency),
      minOrderCents: inputToMinor(minOrder, currency),
      deliveryMinutes: mins,
      polygon: ring as unknown,
    };
    submittingRef.current = true;
    const onSuccess = () => {
      toast(zone ? zf.updated : zf.added);
      onDone();
    };
    const onError = (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : zf.saveFailed);
    // Reset on settle so a failed save can be retried (success navigates away).
    const onSettled = () => {
      submittingRef.current = false;
    };

    if (zone) {
      update.mutate({ zoneId: zone.id, data: shared }, { onSuccess, onError, onSettled });
    } else {
      create.mutate({ ...shared, mode: 'POLYGON' }, { onSuccess, onError, onSettled });
    }
  };

  return (
    <PageContent>
      <MapboxPolygonEditor
        value={ring}
        center={center}
        otherZones={otherZones}
        onChange={setRing}
      />
      <BlockFooter>{zf.drawFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={zf.nameLabel}
          type="text"
          placeholder={zf.namePlaceholder}
          value={label}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
        />
        <ListInput
          label={`${zf.feeLabel} (${currency})`}
          type="text"
          inputmode="decimal"
          placeholder={zf.feePlaceholder}
          value={fee}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          errorMessage={zf.invalidNumber}
          errorMessageForce={!isDecimalInput(fee)}
          onInput={(e) => setFee(sanitizeNumericInput((e.target as HTMLInputElement).value))}
        />
        <ListInput
          label={`${zf.minOrderLabel} (${currency})`}
          type="text"
          inputmode="decimal"
          placeholder={zf.minOrderPlaceholder}
          value={minOrder}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          errorMessage={zf.invalidNumber}
          errorMessageForce={!isDecimalInput(minOrder)}
          onInput={(e) => setMinOrder(sanitizeNumericInput((e.target as HTMLInputElement).value))}
        />
        <ListInput
          label={zf.deliveryTimeLabel}
          type="text"
          inputmode="numeric"
          placeholder={zf.deliveryTimePlaceholder}
          info={zf.deliveryTimeInfo}
          value={deliveryMins}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          errorMessage={zf.wholeMinutes}
          errorMessageForce={!isIntegerInput(deliveryMins)}
          onInput={(e) =>
            setDeliveryMins(sanitizeNumericInput((e.target as HTMLInputElement).value))
          }
        />
      </List>
    </PageContent>
  );
}
